import { RELATED_PAGE_SLUGS } from "./pages-related";
import { legacyCrawlCanonicalPath } from "./crawl-policy";
/**
 * Page Registry — nav metadata for all hand-coded Jyotish pages.
 *
 * Add a new page here → header, footer, and cross-links update
 * automatically.
 *
 * Sitemap and indexing are owned by the D1 `pages` table. The
 * ishvaram-content Worker publishes the canonical sitemap source from D1,
 * and the public R2 static SEO workflow serves the exported sitemap via
 * ishvaram-seo-static. Hand-coded calculator pages should be inserted into
 * D1 with `index_status='indexed'` so they appear in the sitemap alongside
 * programmatic pages.
 */

export interface PageEntry {
  /** URL path without leading slash */
  slug: string;
  /** Display title (short, for nav) */
  title: string;
  /** One-line description (for cross-links, tooltips) */
  description: string;
  /** Emoji/icon for cross-link cards */
  icon: string;
  /** Navigation category */
  category: "calculator" | "rashifal" | "reference" | "dosha" | "tool";
  /** Show as standalone link in header nav bar (daily-use pages only) */
  headerNav?: boolean;
  /** Show inside "Tools ▾" dropdown in header */
  toolsDropdown?: boolean;
  /** Priority for sorting (higher = first). Default 50. */
  priority?: number;
  /** Is this a Hindi page? */
  hindi?: boolean;
  /** Hide from footer (e.g. duplicates like birth-chart ≈ free-kundali) */
  hideFromFooter?: boolean;
  /**
   * Static OG image URL for social sharing.
   * Drives openGraph.images in generateMetadata.
   * Omit to use site default.
   */
  ogImage?: string;
  /**
   * App install CTA behaviour on this page.
   * "strip"   — show AppInstallStrip below content (default for all tool pages)
   * "inline"  — page embeds its own inline CTA (e.g. post-kundali result)
   * false     — suppress entirely (homepage, ai-astrologer which has its own)
   */
  appCTA?: "strip" | "inline" | false;
}

export function pageHref(pageOrSlug: PageEntry | string): string {
  const slug = typeof pageOrSlug === "string" ? pageOrSlug : pageOrSlug.slug;
  const path = slug.startsWith("/") ? slug : `/${slug}/`;
  return legacyCrawlCanonicalPath(path) ?? path;
}

// ─── Page Registry ───────────────────────────────────────────────
// Sorted by display priority. Add new pages at the bottom of each category.

export const PAGE_REGISTRY: PageEntry[] = [
  // ── Calculators (interactive tools) ──
  {
    slug: "free-kundali",
    title: "Free Kundali",
    description: "Complete Vedic birth chart with AI interpretation",
    icon: "✧",
    category: "calculator",
    toolsDropdown: true,
    priority: 100,
  },
  {
    slug: "ai-astrologer",
    title: "AI Astrologer",
    description: "Chat with an AI-powered Vedic astrologer",
    icon: "🤖",
    category: "calculator",
    priority: 98,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "free-astrology-chat",
    title: "Free Astrology Chat",
    description: "Instant AI Jyotish consultation — kundali, dosha, upay, free",
    icon: "💬",
    category: "calculator",
    priority: 97,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "astrologers",
    title: "Our AI Astrologers",
    description: "Meet your AI Vedic astrologer — Parashari Jyotish, full chart reading",
    icon: "✦",
    category: "calculator",
    priority: 97,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "pandits",
    title: "AI Pandit Ji",
    description: "Free Vedic remedies, dosha nivaran, shubh muhurat",
    icon: "ॐ",
    category: "calculator",
    priority: 96,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "talk-to-astrologer",
    title: "Talk to Astrologer",
    description: "Talk to an AI Jyotishi free — kundali, dosha, dasha, upay, no per-minute charges",
    icon: "🗣️",
    category: "calculator",
    priority: 96,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "chat-with-astrologer",
    title: "Chat with Astrologer",
    description: "Chat with an AI Jyotishi online free — instant kundali, dosha, dasha answers",
    icon: "💬",
    category: "calculator",
    priority: 95,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "astrologer-for-marriage-problem",
    title: "Astrologer for Marriage Problem",
    description:
      "Free AI Jyotishi reads your 7th house, Shukra, and Mangal Dosh behind a marriage problem",
    icon: "💍",
    category: "calculator",
    priority: 95,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "career-astrology-consultation",
    title: "Career Astrology Consultation",
    description: "Free AI Jyotishi reads your 10th house, dasha, and transits for career growth",
    icon: "💼",
    category: "calculator",
    priority: 95,
    hideFromFooter: true,
    appCTA: false,
  },
  {
    slug: "ai-kundli",
    title: "AI Kundli",
    description: "AI-powered Janam Kundali with dasha & yogas",
    icon: "✦",
    category: "calculator",
    priority: 96,
    hideFromFooter: true,
  },
  {
    slug: "birth-chart",
    title: "Birth Chart",
    description: "Free Vedic birth chart with AI interpretation",
    icon: "☉",
    category: "calculator",
    priority: 95,
    hideFromFooter: true,
  },
  {
    slug: "kundli-matching",
    title: "Kundli Matching",
    description: "Check compatibility between two horoscopes",
    icon: "♡",
    category: "calculator",
    toolsDropdown: true,
    priority: 90,
  },
  {
    slug: "gun-milan",
    title: "Gun Milan",
    description: "गुण मिलान — 36 Gunas Ashtakoota matching",
    icon: "♡",
    category: "calculator",
    priority: 88,
  },
  {
    slug: "numerology",
    title: "Numerology",
    description: "Find your Mulank, Bhagyank & Name Number",
    icon: "#",
    category: "calculator",
    toolsDropdown: true,
    priority: 80,
  },
  {
    slug: "name-numerology",
    title: "Name Numerology",
    description: "Calculate your name number and personality traits",
    icon: "Aa",
    category: "calculator",
    priority: 78,
  },
  {
    slug: "mulank-calculator",
    title: "Mulank Calculator",
    description: "Find your Mulank (root number) by date of birth",
    icon: "#",
    category: "calculator",
    priority: 79,
  },
  {
    slug: "bhagyank-calculator",
    title: "Bhagyank Calculator",
    description: "Find your Bhagyank (destiny number) by date of birth",
    icon: "#",
    category: "calculator",
    priority: 77,
  },
  {
    slug: "rashi-calculator",
    title: "Rashi Calculator",
    description: "Find your Moon sign (Rashi) by date of birth",
    icon: "☽",
    category: "calculator",
    toolsDropdown: true,
    priority: 75,
  },
  {
    slug: "nakshatra-calculator",
    title: "Nakshatra Calculator",
    description: "Discover your birth star and nakshatra pada",
    icon: "☆",
    category: "calculator",
    toolsDropdown: true,
    priority: 74,
  },
  {
    slug: "baby-names",
    title: "Baby Names",
    description: "Find baby names by nakshatra with numerology scoring",
    icon: "👶",
    category: "calculator",
    toolsDropdown: true,
    priority: 72,
  },
  {
    slug: "mangal-dosha-calculator",
    title: "Mangal Dosha Check",
    description: "Check if you are Manglik and find remedies",
    icon: "♂",
    category: "calculator",
    toolsDropdown: true,
    priority: 73,
  },
  {
    slug: "ascendant-calculator",
    title: "Ascendant Calculator",
    description: "Calculate your Lagna (rising sign)",
    icon: "↑",
    category: "calculator",
    toolsDropdown: true,
    priority: 70,
  },
  {
    slug: "dasha-calculator",
    title: "Dasha Calculator",
    description: "Vimshottari Mahadasha periods from Moon nakshatra",
    icon: "⧗",
    category: "calculator",
    toolsDropdown: true,
    priority: 68,
  },
  {
    slug: "dasamsa",
    title: "Dasamsa Chart",
    description: "D10 career chart calculator and interpretation",
    icon: "▦",
    category: "calculator",
    priority: 66,
  },
  {
    slug: "prashna-kundali",
    title: "Prashna Kundali",
    description: "Horary astrology chart for a question asked now",
    icon: "?",
    category: "calculator",
    priority: 64,
  },
  {
    slug: "hi/dasamsa",
    title: "दशांश चार्ट",
    description: "करियर के लिए D10 दशांश चार्ट कैलकुलेटर",
    icon: "▦",
    category: "calculator",
    priority: 66,
    hindi: true,
  },
  {
    slug: "hi/prashna-kundali",
    title: "प्रश्न कुंडली",
    description: "अभी पूछे गए प्रश्न के लिए होररी ज्योतिष चार्ट",
    icon: "?",
    category: "calculator",
    priority: 64,
    hindi: true,
  },

  // ── Rashifal (daily traffic — standalone in header) ──
  {
    slug: "rashifal",
    title: "Rashifal",
    description: "Daily horoscope for all 12 zodiac signs",
    icon: "♋",
    category: "rashifal",
    headerNav: true,
    priority: 95,
  },
  {
    slug: "weekly-rashifal",
    title: "Weekly Rashifal",
    description: "Weekly horoscope predictions",
    icon: "📅",
    category: "rashifal",
    priority: 80,
  },
  {
    slug: "monthly-rashifal",
    title: "Monthly Rashifal",
    description: "Monthly horoscope forecast",
    icon: "📆",
    category: "rashifal",
    priority: 78,
  },
  {
    slug: "yearly-rashifal",
    title: "Yearly Rashifal",
    description: "Yearly horoscope predictions for all 12 rashis",
    icon: "📆",
    category: "rashifal",
    priority: 76,
  },
  {
    slug: "love-horoscope",
    title: "Love Horoscope",
    description: "Love predictions by sign and year",
    icon: "♡",
    category: "rashifal",
    priority: 74,
  },
  {
    slug: "marriage-horoscope",
    title: "Marriage Horoscope",
    description: "Marriage predictions by sign and year",
    icon: "♡",
    category: "rashifal",
    priority: 73,
  },
  {
    slug: "career-horoscope",
    title: "Career Horoscope",
    description: "Career predictions by sign and year",
    icon: "↑",
    category: "rashifal",
    priority: 72,
  },
  {
    slug: "health-horoscope",
    title: "Health Horoscope",
    description: "Health predictions by sign and year",
    icon: "✦",
    category: "rashifal",
    priority: 71,
  },

  // ── Tools (daily-use — standalone in header) ──
  {
    slug: "panchang",
    title: "Panchang",
    description: "Daily Hindu calendar with tithi and nakshatra",
    icon: "☉",
    category: "tool",
    headerNav: true,
    priority: 92,
  },
  {
    slug: "rahu-kaal",
    title: "Rahu Kaal",
    description: "Today's Rahu Kaal timing and inauspicious periods",
    icon: "⏰",
    category: "tool",
    toolsDropdown: true,
    priority: 90,
  },
  {
    slug: "muhurat",
    title: "Shubh Muhurat",
    description: "Find auspicious timings for today",
    icon: "⏰",
    category: "tool",
    toolsDropdown: true,
    priority: 85,
  },
  {
    slug: "muhurat-report",
    title: "Muhurat Report",
    description: "Personal muhurat reading with city panchang, avoid windows and sankalp",
    icon: "⏰",
    category: "tool",
    priority: 64,
  },
  {
    slug: "choghadiya",
    title: "Choghadiya",
    description: "Today's auspicious & inauspicious time periods",
    icon: "⏳",
    category: "tool",
    toolsDropdown: true,
    priority: 88,
  },
  {
    slug: "tithi-today",
    title: "Aaj Ki Tithi",
    description: "Today's Hindu Tithi, Paksha, and significance",
    icon: "🌙",
    category: "tool",
    toolsDropdown: true,
    priority: 87,
  },
  {
    slug: "shani-transit",
    title: "Shani Transit",
    description: "Saturn's current position and affected Moon signs",
    icon: "♄",
    category: "tool",
    toolsDropdown: true,
    priority: 82,
  },
  {
    slug: "calendar",
    title: "Hindu Calendar",
    description: "Festival & Vrat calendar with puja vidhi",
    icon: "🗓",
    category: "tool",
    headerNav: true,
    priority: 93,
  },
  {
    slug: "prashna",
    title: "Prashna Kundali",
    description: "Ask any question — horary Vedic astrology with no birth details needed",
    icon: "❓",
    category: "tool",
    toolsDropdown: true,
    priority: 55,
  },
  {
    slug: "app",
    title: "Ishvaram",
    description: "Public app information, Google sign-in purpose, and support links",
    icon: "▣",
    category: "tool",
    priority: 40,
    appCTA: false,
  },

  // ── Tarot ──
  {
    slug: "tarot",
    title: "Daily Tarot",
    description: "Free daily tarot card reading with Vedic interpretations",
    icon: "🃏",
    category: "tool",
    toolsDropdown: true,
    priority: 83,
  },

  // ── Transit tracking ──
  {
    slug: "transits",
    title: "Grah Gochar",
    description: "Live planetary transits and personalized transit alerts",
    icon: "🪐",
    category: "calculator",
    toolsDropdown: true,
    priority: 74,
  },

  // ── Dosha & Yoga (content pages) ──
  {
    slug: "remedies",
    title: "Dosha Remedies",
    description: "Personalized remedy bundles for your doshas",
    icon: "🙏",
    category: "dosha",
    priority: 77,
  },
  {
    slug: "kaal-sarp-dosha",
    title: "Kaal Sarp Dosha",
    description: "Types, effects & remedies for Kaal Sarp Dosh",
    icon: "🐍",
    category: "dosha",
    priority: 75,
  },
  {
    slug: "kaal-sarp-dosh",
    title: "Kaal Sarp Dosh Calculator",
    description: "Engine-computed Kaal Sarp Dosh check from your planetary positions",
    icon: "🐍",
    category: "dosha",
    priority: 74,
  },
  {
    slug: "sade-sati",
    title: "Sade Sati",
    description: "Saturn's 7.5 year transit over your Moon sign",
    icon: "🪐",
    category: "dosha",
    priority: 72,
  },
  {
    slug: "sade-sati-report",
    title: "Sade Sati Report",
    description: "Private Shani phase reading with dasha context and Sade Sati upay",
    icon: "♄",
    category: "dosha",
    priority: 71,
  },
  {
    slug: "lal-kitab",
    title: "Lal Kitab",
    description: "Lal Kitab house-based remedies and Rin analysis",
    icon: "📖",
    category: "dosha",
    priority: 74,
  },
  {
    slug: "pitra-dosha",
    title: "Pitra Dosha",
    description: "Ancestral karmic debt — symptoms, detection & remedies",
    icon: "🕉️",
    category: "dosha",
    priority: 73,
  },
  {
    slug: "pitra-dosh-calculator",
    title: "Pitra Dosh Calculator",
    description: "Engine-computed Pitra Dosh check from your planetary positions",
    icon: "🕉️",
    category: "dosha",
    priority: 72,
  },
  {
    slug: "pitra-dosh-report",
    title: "Pitra Dosh Report",
    description: "Private ancestral-karma reading with severity, cancellation and upay sequence",
    icon: "ॐ",
    category: "dosha",
    priority: 71,
  },
  {
    slug: "dosha-remedy-plan",
    title: "Dosha Remedy Plan",
    description: "Chart-specific dosha severity, cancellation and safe upay plan",
    icon: "🙏",
    category: "dosha",
    priority: 70,
  },
  {
    slug: "upay",
    title: "Upay",
    description: "Vedic astrology remedies — mantra, daan, gemstone & sacred items",
    icon: "🙏",
    category: "dosha",
    toolsDropdown: true,
    priority: 76,
  },
  {
    slug: "ashtakvarga",
    title: "Ashtakvarga",
    description: "Planet-strength bindu system — Sarvashtakvarga reading",
    icon: "🎯",
    category: "reference",
    priority: 55,
  },
  {
    slug: "varshphal",
    title: "Varshphal (Solar Return)",
    description: "Your yearly horoscope via Tajik Varshphal system",
    icon: "📆",
    category: "reference",
    priority: 56,
  },
  {
    slug: "kp-astrology",
    title: "KP Astrology",
    description: "Krishnamurti Paddhati — sub-lord stellar astrology",
    icon: "🔭",
    category: "reference",
    priority: 57,
  },
  {
    slug: "vastu",
    title: "Vastu Shastra",
    description: "Vedic architecture — room placement, doshas & remedies",
    icon: "🏠",
    category: "reference",
    priority: 68,
  },
  {
    slug: "gemstones",
    title: "Gemstones",
    description: "Navaratna gemstones by planet, benefits, and cautions",
    icon: "◇",
    category: "reference",
    priority: 67,
  },
  {
    slug: "hi/gemstones",
    title: "रत्न",
    description: "ग्रहों के अनुसार नवरत्न, लाभ और सावधानियां",
    icon: "◇",
    category: "reference",
    priority: 67,
    hindi: true,
  },

  // ── Reference (index pages) ──
  {
    slug: "rashi",
    title: "Rashi Guide",
    description: "All 12 zodiac signs explained",
    icon: "♈",
    category: "reference",
    priority: 60,
  },
  {
    slug: "nakshatra",
    title: "Nakshatra Guide",
    description: "All 27 birth stars explained",
    icon: "⭐",
    category: "reference",
    priority: 58,
  },
  {
    slug: "graha",
    title: "Graha Guide",
    description: "9 planets in Vedic astrology",
    icon: "🌍",
    category: "reference",
    priority: 56,
  },
  {
    slug: "ekadashi",
    title: "Ekadashi Dates",
    description: "Ekadashi vrat dates with paran timing & fasting rules",
    icon: "🌙",
    category: "reference",
    priority: 54,
  },
  {
    slug: "amavasya",
    title: "Amavasya Dates",
    description: "Amavasya (new moon) dates with Pitru Tarpan & shradh timing",
    icon: "🌑",
    category: "reference",
    priority: 54,
  },
  {
    slug: "purnima",
    title: "Purnima Dates",
    description: "Purnima (full moon) dates with Satyanarayan vrat & puja timing",
    icon: "🌕",
    category: "reference",
    priority: 54,
  },
  {
    slug: "pradosh",
    title: "Pradosh Vrat Dates",
    description: "Pradosh vrat dates (Trayodashi) with Shiva puja & twilight timing",
    icon: "🔱",
    category: "reference",
    priority: 54,
  },
  {
    slug: "palmistry",
    title: "Palmistry (Hast Rekha)",
    description: "Read your palm — life, heart, head & fate lines + the planetary mounts",
    icon: "✋",
    category: "reference",
    priority: 52,
  },
  {
    slug: "numerology/life-path",
    title: "Life Path Number (Bhagyank)",
    description: "Your destiny number from your full birth date — meaning of 1–9 & master numbers",
    icon: "🔢",
    category: "reference",
    priority: 51,
  },
  {
    slug: "rudraksha",
    title: "Rudraksha",
    description: "Which mukhi to wear, by planet — meanings, benefits & how to wear",
    icon: "📿",
    category: "reference",
    priority: 50,
  },
  {
    slug: "sankashti-chaturthi",
    title: "Sankashti Chaturthi",
    description: "Sankashti Chaturthi vrat dates with Chandra Darshan & Ganesh puja vidhi",
    icon: "🐘",
    category: "reference",
    priority: 54,
  },
  {
    slug: "hi/ekadashi",
    title: "एकादशी तिथि",
    description: "एकादशी व्रत की तिथियाँ, पारण समय और व्रत नियम",
    icon: "🌙",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "hi/amavasya",
    title: "अमावस्या तिथि",
    description: "अमावस्या की तिथियाँ, पितृ तर्पण और श्राद्ध विधि",
    icon: "🌑",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "hi/purnima",
    title: "पूर्णिमा तिथि",
    description: "पूर्णिमा की तिथियाँ, सत्यनारायण व्रत और पूजा विधि",
    icon: "🌕",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "hi/pradosh",
    title: "प्रदोष व्रत",
    description: "प्रदोष (त्रयोदशी) शिव व्रत की तिथियाँ और पूजा विधि",
    icon: "🔱",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "hi/sankashti-chaturthi",
    title: "संकष्टी चतुर्थी",
    description: "संकष्टी चतुर्थी व्रत की तिथियाँ, चंद्र दर्शन और गणेश पूजा विधि",
    icon: "🐘",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "masik-shivratri",
    title: "Masik Shivratri",
    description: "Masik Shivratri vrat dates with the night vigil & Shiva puja vidhi",
    icon: "🔱",
    category: "reference",
    priority: 54,
  },
  {
    slug: "hi/masik-shivratri",
    title: "मासिक शिवरात्रि",
    description: "मासिक शिवरात्रि व्रत की तिथियाँ, रात्रि जागरण और शिव पूजा विधि",
    icon: "🔱",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "sankranti",
    title: "Sankranti Dates",
    description: "All 12 solar Sankranti dates with Makar Sankranti & snan-daan vidhi",
    icon: "☀",
    category: "reference",
    priority: 54,
  },
  {
    slug: "hi/sankranti",
    title: "संक्रांति तिथि",
    description: "सभी 12 सूर्य संक्रांति तिथियाँ, मकर संक्रांति और स्नान-दान विधि",
    icon: "☀",
    category: "reference",
    priority: 54,
    hindi: true,
  },
  {
    slug: "vrat-calendar",
    title: "Vrat Calendar",
    description: "Every Hindu vrat & fast in one place — Ekadashi to Sankranti",
    icon: "📅",
    category: "reference",
    priority: 56,
  },
  {
    slug: "hi/vrat-calendar",
    title: "व्रत कैलेंडर",
    description: "हर हिंदू व्रत और उपवास एक जगह — एकादशी से संक्रांति तक",
    icon: "📅",
    category: "reference",
    priority: 56,
    hindi: true,
  },
  {
    slug: "dosha",
    title: "Dosha Guide",
    description: "All doshas explained with remedies",
    icon: "⚠",
    category: "reference",
    priority: 55,
  },
  {
    slug: "yoga",
    title: "Yoga Guide",
    description: "Auspicious planetary yogas in Vedic astrology",
    icon: "✨",
    category: "reference",
    priority: 54,
  },
  {
    slug: "daily-horoscope",
    title: "Daily Horoscope",
    description: "Get your free daily Vedic astrology prediction by rashi",
    icon: "✦",
    category: "rashifal",
    priority: 50,
  },
  {
    slug: "dhanteras-2026",
    title: "Dhanteras 2026",
    description: "Discover Dhanteras 2026 date (7 Nov), rituals, and significance",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "diwali-2026",
    title: "Diwali 2026",
    description: "Diwali 2026 dates (9 Nov), Lakshmi Puja timings, and planetary influences",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "dussehra-2026",
    title: "Dussehra 2026",
    description: "Discover Dussehra 2026 dates, muhurat timings, and Vedic significance",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "ganesh-chaturthi-2026",
    title: "Ganesh Chaturthi 2026",
    description: "Ganesh Chaturthi 2026 on 16 August",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "govardhan-puja-2026",
    title: "Govardhan Puja 2026",
    description: "Govardhan Puja 2026: Celebrate on 10th November",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "karwa-chauth-2026",
    title: "Karwa Chauth 2026",
    description:
      "Know the exact date, tithi, and auspicious timings for Karwa Chauth 2026 (29 October)",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/ketu",
    title: "Ketu Mahadasha",
    description: "Why does Ketu's 7-year Mahadasha disrupt careers/relationships?",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/rahu",
    title: "Rahu Mahadasha",
    description: "Why does Rahu's 18-year Mahadasha bring sudden gains & turmoil?",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/sun",
    title: "Surya Mahadasha",
    description:
      "Discover how Surya Mahadasha (6 years) shapes leadership, vitality, and life purpose",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "raksha-bandhan-2026",
    title: "Raksha Bandhan 2026",
    description:
      "Discover Raksha Bandhan 2026 date (29 July), tithi, muhurat, and Vedic astrology insights for siblings' bond",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "today-horoscope",
    title: "Today’s Horoscope",
    description:
      "Get free daily horoscope predictions for all 12 zodiac signs (Rashis) with Vedic astrology insights",
    icon: "✦",
    category: "rashifal",
    priority: 50,
  },
  {
    slug: "tomorrow-horoscope",
    title: "Tomorrow’s Horoscope",
    description: "Get daily Vedic astrology predictions for all 12 rashis",
    icon: "✦",
    category: "rashifal",
    priority: 50,
  },
  {
    slug: "holi-2027",
    title: "Holi 2027",
    description: "Learn Holi 2027 date (4 March), muhurat, rituals & significance",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "ketu-transit",
    title: "Ketu Transit",
    description: "Understand Ketu transit effects on your kundali",
    icon: "✦",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/mars",
    title: "Mangal Mahadasha",
    description: "Why does Mangal Mahadasha bring sudden challenges?",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "manglik-calculator",
    title: "Manglik Calculator",
    description:
      "Check Mangal Dosha (Mars affliction) in your kundali using our free Manglik calculator",
    icon: "✧",
    category: "tool",
    priority: 50,
  },
  {
    slug: "mercury-retrograde",
    title: "Mercury Retrograde",
    description: "Struggling with miscommunication, delays?",
    icon: "✦",
    category: "reference",
    priority: 50,
  },
  {
    slug: "nadi-astrology",
    title: "Nadi Astrology",
    description:
      "Discover how South Indian Nadi astrology predicts life's path through ancient palm-leaf manuscripts",
    icon: "✧",
    category: "tool",
    priority: 50,
  },
  {
    slug: "navratri-2026",
    title: "Navratri 2026",
    description:
      "Discover Sharad Navratri 2026 dates (12th Sept), tithi, puja rituals, and astrological insights for Maa Durga's worship",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "rahu-transit",
    title: "Rahu Transit",
    description: "Rahu transit effects on lagna & rashi, with remedies",
    icon: "✦",
    category: "reference",
    priority: 50,
  },
  {
    slug: "saturn-transit",
    title: "Saturn Transit",
    description:
      "Understand Shani Gochar 2025–26: How Saturn's transit impacts career, health, relationships",
    icon: "✦",
    category: "reference",
    priority: 50,
  },
  {
    slug: "venus-transit",
    title: "Venus Transit",
    description:
      "Understand Shukra Gochar 2026: Venus transit effects, remedies, and timing for wealth, love, health",
    icon: "✦",
    category: "reference",
    priority: 50,
  },
  {
    slug: "chhath-puja-2026",
    title: "Chhath Puja 2026",
    description: "Chhath Puja on 15 Nov 2026 honors Surya & Chandra",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "jupiter-transit",
    title: "Jupiter Transit",
    description:
      "Guru Gochar – how Jupiter's transit shapes dharma, wealth and wisdom across all rashis",
    icon: "✦",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha",
    title: "Vimshottari Mahadasha",
    description:
      "Discover how the 120-year Vimshottari Mahadasha system governs life phases through 9 planets",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "annual-forecast",
    title: "Annual Forecast",
    description: "Personal varshphal: month-by-month dasha and transit timing for the year ahead",
    icon: "✧",
    category: "reference",
    priority: 64,
  },
  {
    slug: "mahadasha-forecast",
    title: "Mahadasha Forecast",
    description: "Current mahadasha, antardasha transitions and 12-month timing",
    icon: "☽",
    category: "reference",
    priority: 66,
  },
  {
    slug: "career-astrology-report",
    title: "Career Report",
    description: "Private career astrology reading for D10, Shani, dasha and wealth yogas",
    icon: "↑",
    category: "reference",
    priority: 65,
  },
  {
    slug: "mahadasha/jupiter",
    title: "Jupiter Mahadasha",
    description: "Explore the transformative power of Guru (Jupiter) Mahadasha (16 years)",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/mercury",
    title: "Budh Mahadasha",
    description:
      "Understand Budh (Mercury) Mahadasha's 17-year impact on communication, intellect, and daily life",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "bhai-dooj-2026",
    title: "Bhai Dooj 2026",
    description:
      "Celebrate Bhai Dooj 2026 with sibling rituals, tilak, gifts, and muhurat guidance",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "janmashtami-2026",
    title: "Janmashtami 2026",
    description: "Krishna Janmashtami 2026 date, puja timings, vrat details, and significance",
    icon: "🪔",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/moon",
    title: "Chandra Mahadasha",
    description:
      "Discover Chandra's 10-year Mahadasha period, its impact on mind/emotions, and Vedic remedies (upay) from Brihat Parashar",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/shani",
    title: "Shani Mahadasha",
    description:
      "Understand Shani's 19-year Mahadasha cycle - its karmic lessons, remedies during affliction, and how Saturn's transit im",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
  {
    slug: "mahadasha/venus",
    title: "Shukra Mahadasha",
    description:
      "Discover Shukra Mahadasha's 20-year influence on wealth, relationships, and creativity",
    icon: "✧",
    category: "reference",
    priority: 50,
  },
];

/** Pages shown as standalone links in header nav (daily-use only: Rashifal, Panchang, Calendar) */
export function getHeaderPages(): PageEntry[] {
  return PAGE_REGISTRY.filter((p) => p.headerNav && !p.hindi).sort(
    (a, b) => (b.priority ?? 50) - (a.priority ?? 50)
  );
}

/** Pages shown in the "Tools ▾" header dropdown (top 14 by priority) */
export function getToolsDropdownPages(): PageEntry[] {
  return PAGE_REGISTRY.filter((p) => p.toolsDropdown && !p.hindi)
    .sort((a, b) => (b.priority ?? 50) - (a.priority ?? 50))
    .slice(0, 14);
}

/** Pages grouped by category for footer */
export function getFooterGroups(): Record<string, PageEntry[]> {
  const groups: Record<string, PageEntry[]> = {};
  for (const page of PAGE_REGISTRY) {
    if (page.hindi || page.hideFromFooter) continue;
    const cat = page.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(page);
  }
  for (const cat of Object.keys(groups)) {
    groups[cat].sort((a, b) => (b.priority ?? 50) - (a.priority ?? 50));
  }
  return groups;
}

/** Category display names for footer headings */
export const CATEGORY_LABELS: Record<string, string> = {
  calculator: "Calculators",
  rashifal: "Rashifal",
  tool: "Daily Tools",
  dosha: "Doshas & Remedies",
  reference: "Learn",
};

/** Semantic cross-link map — ordered by relevance */

/** All pages for cross-link component (excluding current).
 *  Semantically related pages appear first; remainder is priority-sorted. */
export function getCrossLinkPages(currentSlug: string): PageEntry[] {
  const relatedSlugs = RELATED_PAGE_SLUGS[currentSlug] ?? [];
  const relatedPages = relatedSlugs
    .map((slug) => PAGE_REGISTRY.find((p) => p.slug === slug))
    .filter((p): p is PageEntry => p != null && !p.hindi);

  const relatedSet = new Set([currentSlug, ...relatedSlugs]);
  const rest = PAGE_REGISTRY.filter(
    (p) => !relatedSet.has(p.slug) && !p.hindi && p.category !== "reference"
  ).sort((a, b) => (b.priority ?? 50) - (a.priority ?? 50));

  return [...relatedPages, ...rest];
}

/** All pages not in header or Tools dropdown (used as fallback "More" list in mobile) */
export function getMorePages(): PageEntry[] {
  return PAGE_REGISTRY.filter(
    (p) => !p.headerNav && !p.toolsDropdown && !p.hindi && !p.hideFromFooter
  ).sort((a, b) => (b.priority ?? 50) - (a.priority ?? 50));
}
